/**
 * Query Key Factory for USASpending data
 *
 * Provides consistent, type-safe query keys for react-query.
 * Following the factory pattern recommended by TanStack Query.
 */

export interface ListParams {
  page?: number
  limit?: number
  search?: string
  sort?: string
  order?: string
  category?: string
}

/**
 * Query keys for USASpending data
 */
export const usaspendingKeys = {
  // Root key for all USASpending queries
  all: ['usaspending'] as const,

  // Award keys
  awards: () => [...usaspendingKeys.all, 'awards'] as const,
  award: (id: string) => [...usaspendingKeys.awards(), id] as const,
  awardDetail: (id: string) =>
    [...usaspendingKeys.award(id), 'detail'] as const,
  awardPreview: (id: string) =>
    [...usaspendingKeys.award(id), '_preview'] as const,
  awardTransactions: (id: string, page?: number) =>
    [...usaspendingKeys.award(id), 'transactions', { page }] as const,
  awardSubawards: (id: string, page?: number) =>
    [...usaspendingKeys.award(id), 'subawards', { page }] as const,
  awardOrders: (id: string, page?: number) =>
    [...usaspendingKeys.award(id), 'orders', { page }] as const,
  awardOrdersAggregation: (id: string) =>
    [...usaspendingKeys.award(id), 'orders', 'aggregation'] as const,
  awardActivity: (id: string) =>
    [...usaspendingKeys.award(id), 'activity'] as const,
  awardExists: (id: string) =>
    [...usaspendingKeys.awards(), 'exists', id] as const,

  // Recipient keys
  recipients: () => [...usaspendingKeys.all, 'recipients'] as const,
  recipientList: (params?: ListParams) =>
    [...usaspendingKeys.recipients(), 'list', params ?? {}] as const,
  recipient: (id: string) => [...usaspendingKeys.recipients(), id] as const,
  recipientDetail: (id: string) =>
    [...usaspendingKeys.recipient(id), 'detail'] as const,
  recipientPreview: (id: string) =>
    [...usaspendingKeys.recipient(id), '_preview'] as const,
  recipientExists: (id: string) =>
    [...usaspendingKeys.recipients(), 'exists', id] as const,
  // Granular recipient sections for independent Suspense boundaries
  recipientBasicInfo: (id: string) =>
    [...usaspendingKeys.recipient(id), 'basic-info'] as const,
  recipientProfile: (id: string) =>
    [...usaspendingKeys.recipient(id), 'profile'] as const,
  recipientStats: (id: string, filters?: Record<string, unknown>) =>
    [...usaspendingKeys.recipient(id), 'stats', filters ?? {}] as const,
  recipientAnalyticsAgencies: (id: string, filters?: Record<string, unknown>) =>
    [
      ...usaspendingKeys.recipient(id),
      'analytics',
      'agencies',
      filters ?? {},
    ] as const,
  recipientAnalyticsPsc: (id: string, filters?: Record<string, unknown>) =>
    [
      ...usaspendingKeys.recipient(id),
      'analytics',
      'psc',
      filters ?? {},
    ] as const,
  recipientAnalyticsNaics: (id: string, filters?: Record<string, unknown>) =>
    [
      ...usaspendingKeys.recipient(id),
      'analytics',
      'naics',
      filters ?? {},
    ] as const,
  recipientRecentAwards: (id: string, limit?: number) =>
    [...usaspendingKeys.recipient(id), 'awards', { limit }] as const,
  recipientAwardList: (
    recipientHash: string,
    params: { limit: number; category?: string },
  ) =>
    [
      ...usaspendingKeys.recipient(recipientHash),
      'award-list',
      params,
    ] as const,
  recipientInsights: (
    id: string,
    limit?: number,
    filters?: Record<string, unknown>,
  ) =>
    [
      ...usaspendingKeys.recipient(id),
      'insights',
      { limit, ...(filters ?? {}) },
    ] as const,
  recipientHierarchy: (id: string) =>
    [...usaspendingKeys.recipient(id), 'hierarchy'] as const,
  recipientOfficers: (id: string) =>
    [...usaspendingKeys.recipient(id), 'officers'] as const,

  // Agency keys
  agencies: () => [...usaspendingKeys.all, 'agencies'] as const,
  agency: (id: string) => [...usaspendingKeys.agencies(), id] as const,
  agencyDetail: (id: string) =>
    [...usaspendingKeys.agency(id), 'detail'] as const,
  agencyPreview: (id: string) =>
    [...usaspendingKeys.agency(id), '_preview'] as const,
  agencyExists: (id: string) =>
    [...usaspendingKeys.agencies(), 'exists', id] as const,
  // Granular agency sections for independent Suspense boundaries
  agencyBasicInfo: (id: string) =>
    [...usaspendingKeys.agency(id), 'basic-info'] as const,
  agencyProfile: (id: string) =>
    [...usaspendingKeys.agency(id), 'profile'] as const,
  agencyAnalyticsRecipients: (id: string) =>
    [...usaspendingKeys.agency(id), 'analytics', 'recipients'] as const,
  agencyAnalyticsNaics: (id: string) =>
    [...usaspendingKeys.agency(id), 'analytics', 'naics'] as const,
  agencyOpportunities: (id: string) =>
    [...usaspendingKeys.agency(id), 'opportunities'] as const,

  // Person keys
  people: () => [...usaspendingKeys.all, 'people'] as const,
  person: (id: string) => [...usaspendingKeys.people(), id] as const,
  personDetail: (id: string) =>
    [...usaspendingKeys.person(id), 'detail'] as const,
  personPreview: (id: string) =>
    [...usaspendingKeys.person(id), '_preview'] as const,

  // Subaward keys
  subawards: () => [...usaspendingKeys.all, 'subawards'] as const,
  subaward: (id: string) => [...usaspendingKeys.subawards(), id] as const,
  subawardDetail: (id: string) =>
    [...usaspendingKeys.subaward(id), 'detail'] as const,
} as const

/**
 * Query Key Factory for Application DB entities
 *
 * Separate from USASpending keys as these are stored in our
 * application database (federal contracts, grants, DIBBS).
 */
export const appDbKeys = {
  // Root key for all Application DB queries
  all: ['appDb'] as const,

  // Federal Contract keys
  federalContracts: () => [...appDbKeys.all, 'federalContracts'] as const,
  federalContractList: (params?: ListParams) =>
    [...appDbKeys.federalContracts(), 'list', params ?? {}] as const,
  federalContract: (id: string) =>
    [...appDbKeys.federalContracts(), id] as const,
  federalContractDetail: (id: string) =>
    [...appDbKeys.federalContract(id), 'detail'] as const,

  // Federal Grant keys
  federalGrants: () => [...appDbKeys.all, 'federalGrants'] as const,
  federalGrantList: (params?: ListParams) =>
    [...appDbKeys.federalGrants(), 'list', params ?? {}] as const,
  federalGrant: (id: string) => [...appDbKeys.federalGrants(), id] as const,
  federalGrantDetail: (id: string) =>
    [...appDbKeys.federalGrant(id), 'detail'] as const,

  // DIBBS Contract keys
  dibbsContracts: () => [...appDbKeys.all, 'dibbsContracts'] as const,
  dibbsContractList: (params?: ListParams) =>
    [...appDbKeys.dibbsContracts(), 'list', params ?? {}] as const,
  dibbsContract: (id: string) => [...appDbKeys.dibbsContracts(), id] as const,
  dibbsContractDetail: (id: string) =>
    [...appDbKeys.dibbsContract(id), 'detail'] as const,

  // SBIR keys
  sbirs: () => [...appDbKeys.all, 'sbirs'] as const,
  sbirList: (params?: ListParams) =>
    [...appDbKeys.sbirs(), 'list', params ?? {}] as const,
  sbir: (id: string) => [...appDbKeys.sbirs(), id] as const,
  sbirDetail: (id: string) => [...appDbKeys.sbir(id), 'detail'] as const,

  // SLED Contract keys
  sledContracts: () => [...appDbKeys.all, 'sledContracts'] as const,
  sledContractList: (params?: ListParams) =>
    [...appDbKeys.sledContracts(), 'list', params ?? {}] as const,
  sledContract: (id: string) => [...appDbKeys.sledContracts(), id] as const,
  sledContractDetail: (id: string) =>
    [...appDbKeys.sledContract(id), 'detail'] as const,
} as const
